import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MermaidDiagram from './MermaidDiagram';
import { Tooltip, Button, Icon, Table } from '@sm/ide-components';
import { ERole, type ChatMessage, type FeedbackType } from '@/types/chat';
import {
  CopyIcon,
  SaveIcon,
  GradientAiAvatar,
  HelpIcon,
  NoHelpIcon,
  RefreshIcon,
  SwitchChart,
  SwitchTable,
} from '@/assets/SVGIcon';
import { Chart } from '../Chart';
import { useChatStore } from '@/store';
import { agentFeedbackquestion, feedbackQuestion } from '@/services';
import { toast } from '../Message';
import { isAbortError } from '@/utils';
import { getMacawRoot } from '@/utils/macawRoot';
import { t } from '@/i18n';
import { emitGa } from '@/utils/ga';
import { canSaveMindDocument, saveMindDocument } from '@/utils/mindHost';
import { plainTextFromBlocks } from '@/utils/responsesStream';
import MindResponseBlocks, { type MindAskPayload } from './MindResponseBlocks';

interface AssistantCardProps {
  message: ChatMessage;
  onRetry?: (messageId: string) => void;
  /** 分段模式：点击「生成图表」，传入消息自身绑定的 recordId 和 chatId */
  onGenerateChart?: (messageId: string, sqlRecordId: string, sqlChatId: string) => Promise<void>;
  /** 分段模式：点击「分析总结」，传入消息自身绑定的 recordId 和 chatId */
  onSummarize?: (sqlRecordId: string, sqlChatId: string) => Promise<void>;
  /** source 为 Mind 时，在点赞/点踩旁显示复制 */
  showCopy?: boolean;
  /** Mind 追问卡提交。本轮 SSE 还开着时才会成功。 */
  onAnswerAsk?: (questionId: string, payload: MindAskPayload) => Promise<void>;
}

type ChartViewMode = 'chart' | 'table' | 'sql';

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(' ');

function copyWithSelection(text: string): boolean {
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.top = '0';
    area.style.left = '0';
    area.style.opacity = '0';
    getMacawRoot().appendChild(area);
    area.focus();
    area.select();
    area.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    area.remove();
    return ok;
  } catch {
    return false;
  }
}

async function writeClipboard(text: string): Promise<boolean> {
  // 先在点击手势里同步复制。clipboard API 失败后再 await，手势已经失效。
  if (copyWithSelection(text)) return true;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    return false;
  }
  return false;
}

/** 操作栏：重试（仅错误时）、点赞/踩，Mind 时还有复制 */
const ActionBar: React.FC<{
  showRetry?: boolean;
  onRetry: () => void;
  feedback: FeedbackType;
  onFeedback: (type: 'good' | 'bad') => void;
  showCopy?: boolean;
  onCopy?: () => void;
  showSave?: boolean;
  onSave?: () => void;
  copyHint?: string;
  copyHintFailed?: boolean;
}> = ({ showRetry, onRetry, feedback, onFeedback, showCopy, onCopy, showSave, onSave, copyHint, copyHintFailed }) => (
  <div className="msg-action-bar">
    {showRetry && (
      <Button
        type="text"
        size="small"
        icon={<RefreshIcon />}
        className="msg-action-btn"
        onClick={onRetry}
      />
    )}

    <Button
      type="text"
      size="small"
      icon={
        <span style={{ color: feedback === 'bad' ? '#ff4d4f' : '#8694a5', display: 'flex' }}>
          <NoHelpIcon />
        </span>
      }
      className="msg-action-btn"
      onClick={() => onFeedback('bad')}
    />
    <Button
      type="text"
      size="small"
      icon={
        <span style={{ color: feedback === 'good' ? '#ff4d4f' : '#8694a5', display: 'flex' }}>
          <HelpIcon />
        </span>
      }
      className="msg-action-btn"
      onClick={() => onFeedback('good')}
    />
    {showCopy && (
      <>
        <Button
          type="text"
          size="small"
          aria-label={t('chat.copy')}
          title={t('chat.copy')}
          icon={
            <span style={{ color: '#8694a5', display: 'flex' }}>
              <CopyIcon />
            </span>
          }
          className="msg-action-btn"
          onClick={onCopy}
        />
        {showSave && (
          <Button
            type="text"
            size="small"
            aria-label={t('chat.save')}
            title={t('chat.save')}
            icon={
              <span style={{ color: '#8694a5', display: 'flex' }}>
                <SaveIcon />
              </span>
            }
            className="msg-action-btn"
            onClick={onSave}
          />
        )}
        {copyHint ? (
          <span className={`msg-action-copied${copyHintFailed ? ' msg-action-copied--fail' : ''}`}>
            {copyHint}
          </span>
        ) : null}
      </>
    )}
  </div>
);

/** 表格内容渲染 */
const TableView: React.FC<{ data: unknown[] }> = ({ data }) => {
  if (!data.length) {
    return <span className="msg-chart-placeholder__tip">{t('table.noData')}</span>;
  }
  const cols = Object.keys(data[0] as object);
  return (
    <Table
      className="msg-table"
      columns={cols?.map((col) => ({
        title: col,
        dataIndex: col,
        key: col,
      }))}
      dataSource={data as Record<string, unknown>[]}
      pagination={false}
      bordered={false}
      size="small"
      scroll={{ y: 460 }}
      rowKey={(record, index) => String((record as { id?: string | number }).id ?? index)}
      rowClassName="msg-table-row"
    />
  );
};

function isMermaidCode(className: string | undefined): boolean {
  return (className ?? '').split(/\s+/).includes('language-mermaid');
}

function nodeText(children: React.ReactNode): string {
  return React.Children.toArray(children)
    .map((child) => (typeof child === 'string' || typeof child === 'number' ? String(child) : ''))
    .join('')
    .replace(/\n$/, '');
}

const MarkdownBlock: React.FC<{
  className: string;
  text?: string;
}> = ({ className, text }) => {
  if (!text) return null;

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
          code: ({ className: codeClassName, children, node: _node, ...props }) => (
            <code className={codeClassName} {...props}>
              {children}
            </code>
          ),
          pre: ({ children, node: _node, ...props }) => {
            const codeEl = React.Children.toArray(children).find(
              (child) =>
                React.isValidElement<{ className?: string; children?: React.ReactNode }>(child) &&
                isMermaidCode(child.props.className),
            );
            if (React.isValidElement<{ className?: string; children?: React.ReactNode }>(codeEl)) {
              return <MermaidDiagram source={nodeText(codeEl.props.children)} />;
            }
            return <pre {...props}>{children}</pre>;
          },
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
};

const AssistantCard: React.FC<AssistantCardProps> = ({
  message,
  onRetry,
  onGenerateChart,
  onSummarize,
  showCopy,
  onAnswerAsk,
}) => {
  const { updateMessage } = useChatStore();
  const answerRef = useRef<HTMLDivElement>(null);
  const copyTimerRef = useRef<number | undefined>(undefined);
  const savingRef = useRef(false);
  const [copyHint, setCopyHint] = useState('');
  const [copyHintFailed, setCopyHintFailed] = useState(false);
  const isSegmentMessage = message.type === 'segment';
  const { chart, tableData, sql, text, progressText, progressStatus, pendingSqlRecordId, pendingSqlChatId, chartOnly } =
    message.content;
  const feedback = message.feedback ?? null;
  const [isGeneratingChart, setIsGeneratingChart] = useState(false);
  const [chartFailed, setChartFailed] = useState(false);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [summarizeDone, setSummarizeDone] = useState(false);

  const handleGenerateChart = async () => {
    if (!isSegmentMessage) return;
    if (!onGenerateChart || isGeneratingChart) return;
    if (!pendingSqlRecordId || !pendingSqlChatId) return;
    setIsGeneratingChart(true);
    try {
      await onGenerateChart(message.id, pendingSqlRecordId, pendingSqlChatId);
      setChartFailed(false);
      // 成功后 ChatPanel 会将 awaitingChart 置 false，按钮自然消失
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }

      setChartFailed(true);
    } finally {
      setIsGeneratingChart(false);
    }
  };

  const handleSummarize = async () => {
    if (!isSegmentMessage) return;
    if (!onSummarize || isSummarizing) return;
    if (!pendingSqlRecordId || !pendingSqlChatId) return;
    setIsSummarizing(true);
    try {
      await onSummarize(pendingSqlRecordId, pendingSqlChatId);
      setSummarizeDone(true); // 成功后独立隐藏此按钮
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }

      // 失败保留按钮，用户可重试
    } finally {
      setIsSummarizing(false);
    }
  };

  /**
   * 本期：请求进行中时 status='sending'，显示 loading 占位文字，隐藏操作栏。
   * @stream-reserved: 后续流式时 status='streaming'，同样需要隐藏操作栏并显示光标动画。
   */
  const isPending = message.status === 'sending';
  const isRunning = message.status === 'running';
  const isStreaming = message.status === 'streaming';
  const isProgressRunning = progressStatus === 'running';
  const isError = message.status === 'error';
  const isCancelled = message.status === 'cancelled';
  const isBusy = isPending || isRunning || isStreaming;
  const showActionBar = !isBusy && !isError && !isCancelled;
  const progressMarkdownClassName = cx(
    'msg-assistant-text',
    'msg-result',
    'msg-progress-markdown',
    isProgressRunning && 'msg-progress-running',
  );
  const streamMarkdownClassName = cx(
    'msg-assistant-text',
    'msg-result',
    'msg-stream-markdown',
  );
  const progressDisplayText = progressText || (isRunning ? (text || t('chat.running')) : undefined);
  const progressIsPlaceholder = (progressText ?? '').trim() === `- ${t('chat.running')}`;
  const showProgressBlock = Boolean(progressDisplayText) && !(progressIsPlaceholder && !isProgressRunning);
  const showStreamingBlock = Boolean(text && isStreaming);
  const showStaticTextBlock = Boolean(text && !isStreaming && !isRunning);

  /** table 类型只有表格，其余类型有图表+表格切换 */
  const isTableOnly = chart?.type === 'table';
  const defaultView: ChartViewMode = isTableOnly ? 'table' : 'chart';
  const [viewMode, setViewMode] = useState<ChartViewMode>(defaultView);

  const showCopyHint = (hint: string, failed: boolean) => {
    window.clearTimeout(copyTimerRef.current);
    setCopyHint(hint);
    setCopyHintFailed(failed);
    copyTimerRef.current = window.setTimeout(() => {
      setCopyHint('');
      setCopyHintFailed(false);
    }, 1600);
  };

  useEffect(() => () => {
    window.clearTimeout(copyTimerRef.current);
  }, []);

  const replyText = (): string => {
    const stored = (message.content.text ?? '').trim() || plainTextFromBlocks(message.content.blocks ?? []);
    const visible = (answerRef.current?.innerText ?? '').trim();
    return stored || visible;
  };

  const handleCopy = () => {
    const text = replyText();
    if (!text) {
      showCopyHint(t('chat.copyEmpty'), true);
      return;
    }
    void writeClipboard(text).then((ok) => {
      showCopyHint(ok ? t('chat.copied') : t('chat.copyFailed'), !ok);
    });
  };

  const showSave = Boolean(showCopy && canSaveMindDocument());

  const handleSave = () => {
    if (savingRef.current) return;
    const text = replyText();
    if (!text) {
      showCopyHint(t('chat.saveEmpty'), true);
      return;
    }
    savingRef.current = true;
    void saveMindDocument(text).then((outcome) => {
      if (outcome === 'saved') showCopyHint(t('chat.saved'), false);
      else if (outcome === 'invalid') showCopyHint(t('chat.saveEmpty'), true);
      else if (outcome === 'busy') showCopyHint(t('chat.saveBusy'), true);
      else if (outcome === 'failed') showCopyHint(t('chat.saveFailed'), true);
    }).finally(() => {
      savingRef.current = false;
    });
  };

  const handleFeedback = (type: 'good' | 'bad') => {
    if (feedback === type) return;

    emitGa('feedback', { feedback: type });

    const { role: bizRole, accessToken, chatRecordId, sessionId, turnId } = message.bizPayload ?? {};

    if (bizRole === ERole.ANALYST) {
      feedbackQuestion({ accessToken, chatRecordId, comment: type })
        .then(() => {
          updateMessage(message.id, { feedback: type });
        })
        .catch(() => {
          toast.info(t('feedback.failed'));
        });

    } else if (bizRole === ERole.BUSINESS_ANALYSIS) {
      agentFeedbackquestion({ feedback: type, sessionId, turnId }).then(() => {
        updateMessage(message.id, { feedback: type });
      }).catch(() => {
        toast.info(t('feedback.failed'));
      });
    }
  };

  useEffect(() => {
    if (!chart) return;
    setViewMode(chart?.type === 'table' ? 'table' : 'chart');
  }, [chart?.type]);

  const blocks = message.content.blocks;
  if (Array.isArray(blocks)) {
    const hasVisible = blocks.some((block) => block.type !== 'text' || Boolean(block.text.trim()));
    return (
      <div className="msg-assistant-row">
        <div className="msg-assistant-avatar">
          <GradientAiAvatar />
        </div>
        <div className="msg-assistant-content">
          {isPending || (!hasVisible && isBusy) ? (
            <div className="msg-assistant-text msg-loading">
              <span className="msg-loading__dot" />
              <span className="msg-loading__dot" />
              <span className="msg-loading__dot" />
            </div>
          ) : (
            <div ref={answerRef}>
              <MindResponseBlocks
                blocks={blocks}
                live={isBusy}
                onAnswer={onAnswerAsk}
                renderText={(text) => (
                  <MarkdownBlock
                    text={text}
                    className={cx(streamMarkdownClassName, 'msg-markdown-content')}
                  />
                )}
              />
            </div>
          )}
          {isError && (
            <div className="msg-status-label msg-status-label--error">
              {message.statusText || t('chat.analysisFailed')}
            </div>
          )}
          {isCancelled && (
            <div className="msg-status-label msg-status-label--cancel">
              {t('chat.analysisCancelled')}
            </div>
          )}
          {showActionBar && (
            <ActionBar
              showRetry={isError}
              onRetry={() => onRetry?.(message.id)}
              feedback={feedback}
              onFeedback={handleFeedback}
              showCopy={showCopy}
              onCopy={handleCopy}
              showSave={showSave}
              onSave={handleSave}
              copyHint={copyHint}
              copyHintFailed={copyHintFailed}
            />
          )}
        </div>
      </div>
    );
  }

  if (chart) {
    return (
      <div className="msg-card-block">
        <div className="msg-chart-card" ref={answerRef}>
          <div className="msg-chart-header">
            <span className="msg-chart-title">{chart.title}</span>
            {/* chartOnly: recordChat 结果只含 chart schema，无原始数据行，禁止表格切换 */}
            {chart?.type !== 'table' && !chartOnly && (
              <div className="msg-chart-toggle">
                {viewMode === 'table' && (
                  <Button
                    type="text"
                    size="small"
                    icon={<SwitchChart />}
                    className={`msg-view-btn`}
                    onClick={() => setViewMode('chart')}
                  />
                )}
                {viewMode === 'chart' && (
                  <Button
                    type="text"
                    size="small"
                    icon={<SwitchTable />}
                    className={`msg-view-btn`}
                    onClick={() => setViewMode('table')}
                  />
                )}
              </div>
            )}
          </div>

          <div className="msg-chart-body">
            {viewMode === 'chart' && !isTableOnly && (
              <Chart
                data={(chart.data ?? []) as Record<string, unknown>[]}
                type={chart.type as 'bar' | 'line' | 'pie' | 'column'}
                dimensions={chart.dimensions}
                metrics={chart.metrics}
              />
            )}
            {viewMode === 'table' && (
              <div className="msg-table-wrap">
                <TableView data={tableData ?? []} />
              </div>
            )}
            {/* {viewMode === 'sql' && sql && <pre className="msg-sql-block">{sql}</pre>} */}
          </div>
        </div>

        <div className="msg-chart-footer">
          {showActionBar && (
            <ActionBar
              showRetry={isError}
              onRetry={() => onRetry?.(message.id)}
              feedback={feedback}
              onFeedback={handleFeedback}
              showCopy={showCopy}
              onCopy={handleCopy}
              showSave={showSave}
              onSave={handleSave}
              copyHint={copyHint}
              copyHintFailed={copyHintFailed}
            />
          )}
          {/* 分段模式操作按钮组：每个按钮独立控制可见性，只有请求成功后才隐藏 */}
          {!isPending && !isStreaming && isSegmentMessage && (message.awaitingChart || (pendingSqlRecordId && !summarizeDone)) && (
            <div className="msg-segment-actions">
              {/* 「生成图表」：由 awaitingChart 控制；成功后 ChatPanel 将其置 false */}
              {message.awaitingChart && (
                <Button
                  className="msg-generate-chart-btn"
                  size="small"
                  loading={isGeneratingChart}
                  onClick={handleGenerateChart}
                >
                  {chartFailed ? t('chart.retry') : t('chart.generate')}
                </Button>
              )}
              {/* 「分析总结」：由本地 summarizeDone 控制；成功后独立隐藏 */}
              {pendingSqlRecordId && !summarizeDone && (
                <Button
                  className="msg-summarize-btn"
                  size="small"
                  loading={isSummarizing}
                  onClick={handleSummarize}
                >
                  {t('chart.analyze')}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  /** 文字 / 错误 / loading 模式：带头像的气泡 */
  return (
    <div className="msg-assistant-row">
      <div className="msg-assistant-avatar">
        <GradientAiAvatar />
      </div>

      <div className="msg-assistant-content">
        {isPending ? (
          <div className="msg-assistant-text msg-loading">
            <span className="msg-loading__dot" />
            <span className="msg-loading__dot" />
            <span className="msg-loading__dot" />
          </div>
        ) : (
          <div ref={answerRef}>
            {showProgressBlock && (
              <MarkdownBlock
                text={progressDisplayText}
                className={cx(progressMarkdownClassName, 'msg-markdown-content', 'progress')}
              />
            )}
            {showStreamingBlock && (
              <div className={streamMarkdownClassName}>
                <MarkdownBlock text={text} className="msg-markdown-content" />
                <span className="msg-cursor" />
              </div>
            )}
            {showStaticTextBlock && (
              <MarkdownBlock
                text={text}
                className={cx(streamMarkdownClassName, 'msg-markdown-content')}
              />
            )}
          </div>
        )}

        {isError && (
          <div className="msg-status-label msg-status-label--error">
            {message.statusText || t('chat.analysisFailed')}
          </div>
        )}
        {isCancelled && (
          <div className="msg-status-label msg-status-label--cancel">
            {t('chat.analysisCancelled')}
          </div>
        )}

        {showActionBar && (
          <ActionBar
            showRetry={isError}
            onRetry={() => onRetry?.(message.id)}
            feedback={feedback}
            onFeedback={handleFeedback}
            showCopy={showCopy}
            onCopy={handleCopy}
            showSave={showSave}
            onSave={handleSave}
            copyHint={copyHint}
            copyHintFailed={copyHintFailed}
          />
        )}
      </div>
    </div>
  );
};

export default AssistantCard;
